import http from '@/plugins/api'
import * as types from './types'
import { startLoading } from '@/helpers/loading'
import {
  apiUrl,
  isClever,
  isStGobain,
  getCurrentPage,
  isPlatformRagEnabledFromConfig,
  getUsername
} from '@/plugins/globals.js'

import storage from '@/plugins/storage'

import { normalizeMakeName, normalizeModelName } from '@/helpers/vehicle'

const unknownError = 999
function getMahleApiErrorMsg(errorNo, msg) {
  let outputErr = ''
  const genericMsg = 'Check the status of the Wifi connection, try again and if the problem persists, please contact technical assistance.'
  switch (errorNo) {
    case 100: // Generic Unmanaged Error
      outputErr = 'Generic unmanaged error.'
      break
    case 200: // Missing registration Data
      outputErr = `Mandatory data missing: (${msg}).`
      break
    case 204: // No Content
      outputErr = 'No coin data available.'
      break
    case 300: // Login Error
      outputErr = 'Wrong username.'
      break
    case 400: // Missing Astech_sn
      outputErr = 'SN is not associated with any of our devices.'
      break
    case 500: // Missing registration data
      outputErr = 'Missing registration data.'
      break
    case 600: // Missing Or Expired License
      outputErr = 'Diagnostic Licence Expired.'
      break
    default: // Anything else
      outputErr = 'Unknown error.'
      break
  }
  return `${outputErr} \r\n ${genericMsg}`
}

let _latestListReqToken = 0;

export default {
  // TODO: TOMORROW - find out why Scantypes/functions are not being loaded correctly. Also, the rag ratings
  // differ for each make/model combination so we need to ensure that the correct scantypes are being loaded
  // from the server. i.e. double check if it makes sense to cache and load from session storage - I'm not convinced!!
  async initListPreferences({ commit }) {
    const savedSize = await storage.session.getItem(types.REQUEST_PAGE_SIZE);
    if (savedSize) commit(types.SET_PAGE_SIZE, Number(savedSize));

    const savedTerm = await storage.session.getItem(types.REQUEST_SEARCH_TERM);
    if (typeof savedTerm === 'string') commit(types.SET_SEARCH_TERM, savedTerm);

    const savedOrder = await storage.session.getItem(types.REQUEST_ORDER_BY);
    if (savedOrder) commit(types.SET_ORDER_BY, savedOrder);

    const savedDir = await storage.session.getItem(types.REQUEST_SORT_DIR);
    if (savedDir) commit(types.SET_SORT_DIR, savedDir);

    const savedFilter = await storage.local.getItem(types.REQUEST_FILTER_BY);
    if (savedFilter != null) commit(types.SET_FILTER_BY, Number(savedFilter));
  },
  async initLookupsFromStorage({ commit }) {
    const scanTypesRaw = await storage.local.getItem(types.SESSION_SCANTYPES)
    const serviceElementsRaw = await storage.local.getItem(types.SESSION_SERVICEELEMENTS)

    console.groupCollapsed('%c[initLookupsFromStorage] raw strings', 'color:#8a2be2');
    console.log('scanTypesRaw length:', scanTypesRaw?.length ?? 0);
    console.log('serviceElementsRaw length:', serviceElementsRaw?.length ?? 0);
    console.groupEnd();

    try {
      const scanTypes = scanTypesRaw ? JSON.parse(scanTypesRaw) : []
      console.groupCollapsed('%c[initLookupsFromStorage] parsed scantypes BEFORE commit', 'color:#8a2be2');
      console.table((scanTypes || []).slice(0, 10).map((o, i) => ({
        i,
        id: o?.id,
        Id: o?.Id,
        value: o?.value,
        text: o?.text,
        name: o?.name,
        label: o?.label
      })));
      console.groupEnd();

      commit(types.SET_SCANTYPES, scanTypes)
    } catch (e) {
      console.error('Error parsing scantypes:', e)
    }

    try {
      const serviceElements = serviceElementsRaw ? JSON.parse(serviceElementsRaw) : []
      console.groupCollapsed('%c[initLookupsFromStorage] parsed serviceelements BEFORE commit', 'color:#8a2be2');
      console.table((serviceElements || []).slice(0, 10));
      console.groupEnd();

      commit(types.SET_SERVICEELEMENTS, serviceElements)
    } catch (e) {
      console.error('Error parsing serviceelements:', e)
    }
  },

  setSearchTerm({ commit }, text) {
    commit(types.SET_SEARCH_TERM, text)
  },
  setPageSize({ commit }, pageSize) {
    commit(types.SET_PAGE_SIZE, pageSize)
  },
  setSortDir({ commit }, sortDir) {
    commit(types.SET_SORT_DIR, sortDir)
  },
  setOrderBy({ commit }, orderBy) {
    commit(types.SET_ORDER_BY, orderBy)
  },
  setFilterBy({ commit }, filterBy) {
    commit(types.SET_FILTER_BY, filterBy)
  },
  setFilterOptions({ commit }, data) {
    commit(types.SET_FILTER_OPTIONS, data)
  },
  setNewRequest({ commit, rootGetters }) {
    const language = rootGetters['auth/language']
    commit('SET_NEW_REQUEST', language)
  },
  resetVehicle({ commit }) {
    commit('SET_VEHICLEINFO')
  },
  fetchReport({ rootGetters }, id) {
    let language = rootGetters['auth/language']

    if (language && language !== '') {
      if (language.toUpperCase().includes('EL') || language.toUpperCase().includes('GR')) {
        language = 'el-GR'
      } else {
        language = `${language}-${language.toUpperCase()}`
      }
    }

    // Determine client domain name
    let domainName = window.location.hostname.toLowerCase().split('.')[0] || ''
    console.log('Support info clientName: ', domainName)

    // Build base URL
    let url = `${apiUrl()}/Report/CreatePdf/?workorderId=${id}&cultureCode=${language}&domainName=${domainName}`

    if (domainName.includes('repairify') || domainName.includes('uk') || domainName.includes('localhost')) {
      // Direct open for Repairify/UK/local
      window.open(url, '_blank')
    } else {
      // Google Docs viewer for others
      const encodedUrl = `${apiUrl()}/Report/CreatePdf/?workorderId=${id}%26cultureCode=${language}%26domainName=${domainName}`
      window.open(`https://docs.google.com/viewer?url=${encodedUrl}`, '_blank')
    }
  },
  async getScanReport({ dispatch }, data) {
    const r = await startLoading(dispatch, 'getScanReport', () =>
      http.get(`report/CreatePdf?workorderId=${data.id}&cultureCode=${data.code}`)
    )
    return r.data
  },
  async getServiceRequest({ commit, dispatch, rootGetters }, id) {
    const r = await startLoading(dispatch, 'getServiceRequest', () =>
      http.get(`ServiceRequest/ServiceRequest?id=${id}`)
    )
    const language = rootGetters['auth/language']
    const currentPage = await getCurrentPage()
    commit(types.SET_REQUEST, { request: r.data, language, currentPage })
  },
  async getServiceRequests({ commit, dispatch, rootState }, data) {
    // Create a unique token for this invocation
    const uniqueToken = ++_latestListReqToken;

    console.log('DEBUG => [getServiceRequests] data ->', data);
    const pageSize = Number(data.pageSize ?? rootState.serviceRequests.pageSize) || 10;
    const offset = Math.max(1, Number(data.offset) || 1); // 1-based for SQL

    const params = new URLSearchParams({
      search: data.search ?? '',
      offset: String(offset),
      pageSize: String(pageSize),
      orderBy: data.orderBy ?? 'Id',
      sortBy: data.sortBy ?? 'desc',
      status: data.status ?? '',
      location: String(data.location ?? 0),
    });

    console.log('DEBUG => [getServiceRequests] query ->', params.toString());

    const r = await startLoading(dispatch, 'getServiceRequests', () =>
      http.get(`ServiceRequest/ServiceRequestsForList?${params}`)
    );

    // If a newer call started while we were awaiting, bail out (stale response)
    if (uniqueToken !== _latestListReqToken) {
      console.warn('DEBUG => [getServiceRequests] stale response dropped', { uniqueToken, _latestListReqToken });
      return null;
    }

    const rows = Array.isArray(r.data) ? r.data : [];
    const total = rows.length ? rows[0].TotalCount : 0;

    commit(types.SET_REQUESTS, rows);
    commit(types.SET_TOTAL_COUNT, total)
    return rows;
  },

  async cloneServiceRequest({ commit, dispatch, rootGetters }, data) {
    const r = await startLoading(dispatch, 'cloneServiceRequest', () =>
      http.get(`ServiceRequest/CloneServiceRequest?id=${data.id}&userid=${data.userid}`)
    )
    const language = rootGetters['auth/language']
    const currentPage = await getCurrentPage()
    console.log(`DEBUG => [ACTIONS] => cloneServiceRequest response: ${JSON.stringify(r.data)}, language: ${language}, currentPage: ${currentPage}`)
    commit(types.SET_REQUEST, { request: r.data, language, currentPage })
  },
  async getRequestCounts({ commit, dispatch }, id) {
    const r = await startLoading(dispatch, 'getRequestCounts', () =>
      http.get(`ServiceRequest/GetServiceRequestCounts?id=${id}`)
    )
    commit(types.SET_REQUESTCOUNTS, r.data)
    return r.data
  },
  async decodeVin({ commit, dispatch }, vin) {
    const r = await startLoading(dispatch, 'decodeVin', () =>
      http.get(`Decoder/DecodeVinAsTech?vin=${vin}`)
    )
    commit(types.SET_VIN_SUMMARY, r.data)
  },
  async decodeVrm({ commit, dispatch }, vrm) {
    const r = await startLoading(dispatch, 'decodevrm', () =>
      http.get(`Decoder/DecodeVrm?vrm=${vrm}`)
    )
    commit(types.SET_VRM_SUMMARY, r.data)
  },
  async decodeVinUK({ commit, state, dispatch }, vin) {
    if (!vin) return

    const r = await startLoading(dispatch, 'decodeVinUK', () =>
      http.get(`Decoder/DecodeVinAsTech?vin=${vin}`)
    )
    const vinSummary = r.data
    console.log(`DEBUG => [ACTION => decodeVinUk] vinSummary: ${JSON.stringify(vinSummary)}`)
    // We have a Make and Year so let's get the associated Models
    const isPlatformRagEnabled = await isPlatformRagEnabledFromConfig()
    if (isPlatformRagEnabled) {
      // If jatoMakes is set to default i.e. [{"value":-1,"name":"Vin Mismatch"}], then
      // use the year to populate it before continuing!
      console.log('DEBUG => [ACTIONS] => decodeVinUK about to call getJATOVehicleMakes (state.jatoMakes):', state.jatoMakes)
      if (state.jatoMakes.length === 1 && state.jatoMakes[0].name === 'Vin Mismatch') {
        await dispatch('getJATOVehicleMakes', parseInt(vinSummary.yearId))
      }
      console.log('DEBUG => [ACTIONS] => decodeVinUK called getJATOVehicleMakes (state.jatoMakes):', state.jatoMakes)
      if (state.jatoMakes.length > 1 && vinSummary) {
        const makeName = normalizeMakeName(vinSummary.make?.name, isPlatformRagEnabled)
        const modelName = normalizeModelName(vinSummary.model?.name)
        const makeYear = vinSummary.yearId || null

        const jatoMake = makeName
          ? state.jatoMakes.find(make => make.name?.toUpperCase() === makeName.toUpperCase())
          : null
        console.log(`DEBUG => [ACTION->decodeVinUK] jatoMake: ${JSON.stringify(jatoMake)}`)
        // Populate state.jatoModels
        if (jatoMake) {
          if (makeYear && makeYear > 1985) {
            await dispatch('updateRequestRecord', {
              item: 'Year',
              value: parseInt(makeYear)
            })
          }

          // Sending the full object down e.g. {"value":10,"name":"BMW"}
          await dispatch('updateRequestRecord', {
            item: 'Make',
            value: {
              Id: parseInt(jatoMake.value),
              Name: jatoMake.name,
              Text: jatoMake.name
            }
          })

          await dispatch('updateRequestRecord', {
            item: 'MakeName',
            value: jatoMake.name
          })

          await dispatch('getJATOVehicleModels', {
            year: makeYear,
            make: jatoMake.name
          })
        }

        if (
          jatoMake &&
          state.jatoModels.length === 1 &&
          state.jatoModels[0]?.name === 'Vin Mismatch'
        ) {
          await dispatch('getJATOVehicleModels', {
            year: makeYear,
            make: jatoMake.name
          })
        }

        if (
          jatoMake &&
          state.jatoModels.length > 1 &&
          modelName
        ) {
          const jatoModel = state.jatoModels.find(model =>
            model.name?.toUpperCase() === modelName.toUpperCase()
          )
          if (jatoModel) {
            await dispatch('updateRequestRecord', {
              item: 'Model',
              value: {
                Id: parseInt(jatoModel.value),
                Name: jatoModel.name,
                Text: jatoModel.name
              }
            })

            await dispatch('updateRequestRecord', {
              item: 'ModelName',
              value: jatoModel.name
            })
          }
        }
      }
    }

    commit(types.SET_VIN_SUMMARY_UK, vinSummary, isPlatformRagEnabled)
  },
  async getVehicleYears({ commit, dispatch }) {
    const r = await startLoading(dispatch, 'getVehicleYears', () =>
      http.get('ServiceRequest/VehicleYears')
    )
    commit(types.SET_YEARS, r.data)
  },
  async getVehicleMakes({ commit, dispatch }, year) {
    // Retrieves the AAIA makes
    if (!year) {
      console.warn('DEBUG => [ACTIONS] => getVehicleMakes called with invalid year:', year)
      return
    }
    const r = await startLoading(dispatch, 'getVehicleMakes', () =>
      http.get(`ServiceRequest/VehicleMakes?year=${year}`)
    )
    commit(types.SET_MAKES, r.data)
  },
  async getVehicleModels({ commit, dispatch }, data) {
    // Retrieves the AAIA models based on year and make
    if (!data || !data.year || !data.make) {
      console.warn('DEBUG => [ACTIONS] => getVehicleModels called with invalid data:', data)
      return
    }
    const r = await startLoading(dispatch, 'getVehicleModels', () =>
      http.get(`ServiceRequest/VehicleModels?year=${data.year}&make=${data.make}`)
    )
    commit(types.SET_MODELS, r.data)
  },
  async resetJatoModels({ commit }) {
    commit(types.RESET_JATO_MODELS)
  },

  async getJATOVehicleYears({ commit, dispatch }) {
    const r = await startLoading(dispatch, 'getJatoVehicleYears', () =>
      http.get('RAGRating/JatoVehicleYears')
    )
    commit(types.SET_JATO_YEARS, r.data)
  },

  async getJATOVehicleMakes({ commit, dispatch }, year) {
    console.log(`DEBUG => [ACTION->getJATOVehicleMakes] year: ${year}`)
    if (!year) {
      return
    }
    const r = await startLoading(dispatch, 'getJATOVehicleMakes', () =>
      http.get(`RAGRating/JATOVehicleMakes?year=${year}`)
    )
    commit(types.SET_JATO_MAKES, r.data)
    commit(types.RESET_JATO_MODELS)
  },

  async getJATOVehicleModels({ commit, state, dispatch }, data) {
    if (data?.make === 'Vin Mismatch') {
      commit(types.SET_JATO_MODELS, [{ value: -1, name: 'Vin Mismatch' }])
    } else {
      if (!data || !data.year || !data.make) {
        console.warn('DEBUG => [ACTIONS] => getJATOVehicleModels called with invalid data:', data)
        return
      }
      const r = await startLoading(dispatch, 'getJATOVehicleModels', () =>
        http.get(`RAGRating/JATOVehicleModels?year=${data.year}&make=${data.make}`)
      )

      commit(types.SET_JATO_MODELS, r.data)
    }
  },

  async getVehicleSubModels({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'getVehicleSubModels', () =>
      http.get(`ServiceRequest/VehicleSubModels?year=${data.year}&make=${data.make}&model=${data.model}`)
    )

    commit(types.SET_SUBMODELS, r.data)
  },
  async getVehicleEngines({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'getVehicleEngines', () =>
      http.get(`ServiceRequest/VehicleEngines?year=${data.year}&make=${data.make}&model=${data.model}&submodel=${data.submodel}`)
    )

    commit(types.SET_ENGINES, r.data)
  },
  async getVehicleTransmissions({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'getVehicleTransmissions', () =>
      http.get(`ServiceRequest/VehicleTransmissions?year=${data.year}&make=${data.make}&model=${data.model}`)
    )

    commit(types.SET_TRANSMISSIONS, r.data)
  },
  async getVehicleDriveTypes({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'getVehicleDriveTypes', () =>
      http.get(`ServiceRequest/VehicleDriveTypes?year=${data.year}&make=${data.make}&model=${data.model}`)
    )

    commit(types.SET_DRIVETYPES, r.data)
  },
  async getScanTypes({ commit, dispatch }, data) {
    if (!Number.isFinite(Number(data.id)) || Number(data.id) <= 0) {
      console.warn('[getScanTypes] ignored: invalid location id', data.id)
      return
    }
    const r = await startLoading(dispatch, 'getScanTypes', () => {
      if (data.isMahle === false) {
        return http.get(`ServiceRequest/ScanTypes?locationId=${data.id}`)
      } else {
        return http.get(`ServiceRequest/MahleScanTypes?locationId=${data.id}`)
      }
    })
    commit(types.SET_SCANTYPES, r.data)
  },

  async getRagRatingVehicleId({ commit, dispatch }, data) {
    let make = encodeURIComponent(data.make || 'Vin Mismatch')
    let model = encodeURIComponent(data.model || 'Vin Mismatch')
    const r = await startLoading(dispatch, 'getRagRatingVehicleId', () => {
      return http.get(`RagRating/getRagRatingVehicleId?make=${make}&model=${model}&year=${data.year}`)
    })
    commit(types.SET_RAGRATINGVEHICLEID, r.data)
    return r.data
  },

  async getServiceElements({ commit, dispatch }, data) {
    if (data === null || data.scanTypeGroupId === null || data.ragRatingVehicleId === null || data.language === null || data.deviceTypeId === null || data.deviceTypeId === undefined) {
      const data = '[{"Id":-1,"Name":"Any","Translation":null,"RagRatingTypeId":400,"RagRatingLabel":"Green","EthernetCableRequired":false,"EVRequiresCode":false,"CustomMessage":null}]'
      commit(types.SET_SERVICEELEMENTS, data)
      return
    }
    console.log('DEBUG => [ACTION->getServiceElements] => data: ', data)
    const r = await startLoading(dispatch, 'getServiceElements', () => {
      return http.get(`RagRating/GetServiceElementsByScanTypeGroupAndVehicle?scanTypeGroupId=${data.scanTypeGroupId}&ragRatingVehicleId=${data.ragRatingVehicleId}&deviceTypeId=${data.deviceTypeId}&cultureCode=${data.language}`)
    })
    commit(types.SET_SERVICEELEMENTS, r.data)
  },

  async getServiceRequestStatusList({ commit, dispatch }) {
    const r = await startLoading(dispatch, 'getServiceRequestStatusList', () =>
      http.get('ServiceRequest/ServiceRequestStatusList')
    )
    commit(types.SET_STATUSLIST, r.data)
  },

  async createServiceRequest({ commit, dispatch }, request) {
    const r = await startLoading(dispatch, 'createServiceRequest', () =>
      http.post('ServiceRequest/CreateRequest', request)
    )
    commit(types.SET_REQUESTID, r.data)
    return r.data
  },

  async createSaintGobainServiceRequest({ commit, dispatch }, request) {
    const r = await startLoading(dispatch, 'createServiceRequest', () =>
      http.post('ServiceRequest/CreateSaintGobainRequest', request)
    )
    commit(types.SET_REQUESTID, r.data)
    return r.data
  },

  async updateRequestRecord({ commit }, data) {
    const isPlatformRagEnabled = await isPlatformRagEnabledFromConfig()
    const username = await getUsername()
    console.log('DEBUG => [ACTIONS->updateRequestRecord] => data: ', data)
    commit(types.UPDATE_REQUEST_RECORD, { data, isPlatformRagEnabled, userName: username })
  },
  async updateServiceRequest({ commit, dispatch }, request) {
    const r = await startLoading(dispatch, 'updateServiceRequest', () =>
      http.post('ServiceRequest/UpdateRequest', request)
    )
    commit(types.SET_REQUESTID, r.data)
    return r.data
  },
  async updateServiceRequestPriorities({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'updateServiceRequestPriorities', () =>
      http.post('ServiceRequest/UpdateRequestPriority', data)
    )
    commit(types.SET_REQUESTID, r.data)
  },
  async updateServiceRequestStatus({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'updateServiceRequestStatus', () =>
      http.post('ServiceRequest/UpdateRequestStatus', data)
    )
    commit(types.SET_REQUESTID, r.data)
  },
  async resendMahleReportForWorkOrderNo({ commit, dispatch }, workOrderNo) {
    commit(types.RESET_MAHLE_API_ERROR_MSG)
    console.log(`DEBUG => Resending Mahle report for Work Order No: ${workOrderNo}`)
    let result = null
    try {
      result = await startLoading(dispatch, 'ResendMahleReportForWorkOrderNo', () =>
        http.post(`Mahle/ResendMahleReportForWorkOrderNo?workOrderNo=${workOrderNo}`)
      )

      // Api call was successful and hopefully contains the Mahle coin balance.
      console.log(`Api Call response (0): ${JSON.stringify(result)}`)
      if (result && result.status === 200 && result.data.error === 0) {
        return true
      }

      if (!result.data.status || result.data.error !== 0) {
        commit(types.SET_MAHLE_API_ERROR_MSG, getMahleApiErrorMsg(result.data.error, result.data.errorText))
      }
    } catch (e) {
      // Api call was unsuccessful.
      console.log(`Api call returned Error: ${e}`)
      commit(types.SET_MAHLE_API_ERROR_MSG, getMahleApiErrorMsg(result.data.error || unknownError, result.data.errorText || ''))
    }
    return false
  },
  async requestSupportProcessing({ commit, dispatch, rootGetters }, supportRequest) {
    try {
      let language = rootGetters['auth/language']
      if (language) {
        language = `${language}-${language.toUpperCase()}`
      }
      console.log(`DEBUG => Requesting support processing: ${JSON.stringify(supportRequest)}`)
      const result = await startLoading(dispatch, 'RequestSupportProcessing', () =>
        http.post(`Mahle/RequestSupportProcessing?cultureCode=${language}`, supportRequest)
      )

      return result.data
    } catch (e) {
      commit(
        types.SET_MAHLE_API_ERROR_MSG,
        getMahleApiErrorMsg(e.message || unknownError, e.message || '')
      )
      return { data: { status: false, error: e.id, errorText: e.message } }
    }
  },
  async isVinInUseAtCurrentLocation({ commit, dispatch }, data) {
    if (data === null || data.vin === null || data.locationId === null) {
      return { IsVinInUse: false, Results: [] }
    }
    const r = await startLoading(dispatch, 'IsVinInUseAtCurrentLocation', () =>
      http.get(`ServiceRequest/IsVinInUseAtCurrentLocation?vin=${data.vin}&locationId=${data.locationId}`)
    )
    return r.data
  },
  async audit({ commit, dispatch }, auditDetails) {
    if (isClever() || isStGobain()) {
      return
    }
    const res = await startLoading(dispatch, 'Audit', () =>
      http.post('ragrating/Audit', auditDetails)
    )
    return res.data
  },
  totalUnreadChats({ commit }, unread) {
    commit(types.UPDATE_TOTAL_UNREAD, unread)
    commit(types.SET_TOTAL_UNREAD_CHATS, unread)
  },
  updateUnreadChats({ commit }, channelList = []) {
    // commit(types.UPDATE_UNREAD, channelList)
    const entries = []
    for (const ch of channelList || []) {
      // channel.name holds your work order id in your code
      const id = ch?.name ?? null
      if (id != null) {
        entries.push({ id: String(id), count: Number(ch.unreadMessageCount || 0) })
      }
    }
    if (entries.length) {
      commit(types.SET_REQUEST_UNREAD_BULK, entries)
    }
  },
  resetRequestUnread({ commit }, id) {
    commit(types.RESET_REQUEST_UNREAD, id)
  },
  async updateRequestUnread({ commit }, { id, count }) {
    commit(types.SET_REQUEST_UNREAD, { id, count })
  },
  async getServiceElementsRagRating({ commit, dispatch }, data) {
    if (!data.make || !data.model || !data.year || !data.deviceTypeId) {
      console.error('Missing required parameters for RAG service elements lookup:', data)

      // Clear service elements if parameters are missing
      commit(types.SET_RAG_SERVICEELEMENTS, [])
      return []
    }

    // Set default values for optional parameters
    const orderBy = data.orderBy || 'Id'
    const sortBy = data.sortBy || 'ASC'
    const languageCode = data.languageCode || 'en'

    // URL encode the make and model to handle special characters
    const encodedMake = encodeURIComponent(data.make)
    const encodedModel = encodeURIComponent(data.model)

    try {
      // Call the new RAG Rating Controller endpoint
      const response = await startLoading(dispatch, 'getServiceElementsRagRating', () => {
        return http.get(`RAGRating/GetServiceElementsRagRating?make=${encodedMake}&model=${encodedModel}&year=${data.year}&orderBy=${orderBy}&sortBy=${sortBy}&deviceTypeId=${data.deviceTypeId}&languageCode=${languageCode}`)
      })
      commit(types.SET_RAG_SERVICEELEMENTS, response.data)
      console.log('RAG Service Elements loaded successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Error loading RAG service elements:', error)
      // Clear service elements on error - let the UI display appropriate error messages
      commit(types.SET_RAG_SERVICEELEMENTS, [])
      // Re-throw the error so the component can handle it
      throw error
    }
  },
  async getRagDeviceTypes({ commit, dispatch }) {
    const r = await startLoading(dispatch, 'GetRagDeviceTypes', () =>
      http.get('DeviceType/GetAllDeviceTypes')
    )
    // Filter out deleted device types (where DeletedDate is not null)
    const activeDeviceTypes = r.data.filter(device => !device.DeletedDate && device.RAGEnabled === true)
    commit(types.SET_RAG_DEVICETYPES, activeDeviceTypes)
  },
  async getDeviceTypeImage({ dispatch }, deviceId) {
    const response = await startLoading(dispatch, 'getDeviceTypeImage', () =>
      http.get(`DeviceType/GetDeviceTypeImage/${deviceId}`, { responseType: 'blob' })
    )
    return URL.createObjectURL(response.data)
  }
}
